import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../../MyComponents/LandingSections/SectionEyebrow';
import OrangeLogo from '../../../MyComponents/icons/OrangeLogo';
import { CARD, PILL_SECONDARY, TEXT_LINK } from './dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

const PLATFORM_URL = 'https://www.platformadigitaliada.ro/';
const XR_GUIDE_URL = 'https://ghid.platformadigitaliada.ro/resurse-xr/';
const xrUrl = (id) => `https://www.platformadigitaliada.ro/xr/${id}/view`;

const TAG_MOUNTAIN = 'orange.tag.mountains';
const TAG_CARPATHIANS = 'orange.tag.carpathians';
const TAG_GEOGRAPHY = 'orange.tag.geography';
const TAG_COUNTY = 'orange.tag.county';
const TAG_ROMANIA = 'orange.tag.romania';
const TAG_CAVE = 'orange.tag.cave';
const TAG_SFINX = 'orange.tag.sfinx';
const CLASS_VIII = 'orange.class.VIII';

const XR_RESOURCES = [
    { id: 2, tags: [TAG_CARPATHIANS, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 3, tags: [TAG_CARPATHIANS, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 4, tags: [TAG_CARPATHIANS, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 5, tags: [TAG_CARPATHIANS, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 7, tags: [TAG_GEOGRAPHY, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 8, tags: [TAG_SFINX], classId: CLASS_VIII },
    { id: 9, tags: [TAG_CAVE], classId: CLASS_VIII },
    { id: 10, tags: [TAG_CAVE], classId: CLASS_VIII },
    { id: 11, tags: [TAG_CAVE], classId: CLASS_VIII },
    { id: 12, tags: [TAG_CAVE], classId: CLASS_VIII },
    { id: 13, tags: [TAG_CAVE], classId: CLASS_VIII },
    { id: 14, tags: [TAG_CAVE], classId: CLASS_VIII },
    { id: 15, tags: [TAG_GEOGRAPHY, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 16, tags: [TAG_GEOGRAPHY, TAG_ROMANIA], classId: CLASS_VIII },
    { id: 21, tags: [TAG_GEOGRAPHY, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 22, tags: [TAG_GEOGRAPHY, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 23, tags: [TAG_GEOGRAPHY, TAG_COUNTY], classId: CLASS_VIII },
    { id: 24, tags: [TAG_GEOGRAPHY, TAG_COUNTY], classId: CLASS_VIII },
    { id: 25, tags: [TAG_GEOGRAPHY, TAG_COUNTY], classId: CLASS_VIII },
    { id: 26, tags: [TAG_GEOGRAPHY, TAG_COUNTY], classId: CLASS_VIII },
    { id: 27, tags: [TAG_GEOGRAPHY, TAG_COUNTY], classId: CLASS_VIII },
    { id: 28, tags: [TAG_GEOGRAPHY, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 29, tags: [TAG_GEOGRAPHY, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 30, tags: [TAG_GEOGRAPHY, TAG_MOUNTAIN], classId: CLASS_VIII },
    { id: 31, tags: [TAG_GEOGRAPHY, TAG_MOUNTAIN], classId: CLASS_VIII },
];

// Enough to show what the collection is like without the whole list pushing
// the page down; the toggle reveals the rest.
const PREVIEW_COUNT = 6;

const STACK = '@media (max-width: 599.95px)';

const useStyles = makeStyles(() => ({
    card: {
        ...CARD,
        padding: 'clamp(22px, 3vw, 34px)',
    },
    head: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '18px',
        // Side by side leaves the copy a sliver on phones; stack it instead.
        [STACK]: {
            flexDirection: 'column',
            gap: '14px',
        },
    },
    logo: {
        flexShrink: 0,
        width: '48px',
        height: '48px',
        marginTop: '2px',
        borderRadius: '12px',
    },
    eyebrow: {
        marginBottom: '12px',
    },
    title: {
        margin: '0 0 8px',
        fontSize: 'clamp(1.35rem, 2.4vw, 1.8rem)',
        lineHeight: 1.15,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    text: {
        maxWidth: '640px',
        margin: '0 0 16px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.98rem',
        lineHeight: 1.6,
    },
    external: PILL_SECONDARY,
    rule: {
        height: '1px',
        margin: '28px 0 24px',
        background: REDESIGN_COLORS.sand,
    },
    xrHead: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '10px 24px',
        marginBottom: '18px',
    },
    xrTitle: {
        margin: '0 0 6px',
        fontSize: '1.2rem',
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    xrLead: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.92rem',
    },
    xrLink: TEXT_LINK,
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 230px), 1fr))',
        gap: '14px',
    },
    resource: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        boxSizing: 'border-box',
        padding: '16px',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '18px',
        background: REDESIGN_COLORS.cream,
        textDecoration: 'none',
        transition: 'border-color 150ms ease, background 150ms ease',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            background: REDESIGN_COLORS.white,
        },
    },
    resourceClass: {
        color: REDESIGN_COLORS.accent,
        fontSize: '0.7rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
    },
    resourceTitle: {
        margin: 0,
        fontSize: '0.98rem',
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    resourceText: {
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '0.84rem',
        lineHeight: 1.5,
    },
    tags: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: 'auto',
        paddingTop: '4px',
    },
    tag: {
        padding: '3px 9px',
        borderRadius: '999px',
        color: REDESIGN_COLORS.link,
        background: REDESIGN_COLORS.mist,
        fontSize: '0.72rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    toggleRow: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '20px',
    },
    toggle: PILL_SECONDARY,
}));

/**
 * The Orange Foundation's Digitaliada material: a pointer to the platform and
 * its XR (360°) geography resources, each opening on the platform in a new tab.
 */
const DigitaliadaContent = () => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);

    const visible = expanded ? XR_RESOURCES : XR_RESOURCES.slice(0, PREVIEW_COUNT);

    return (
        <LandingSection id="digitaliada" background="none" gutter={0} paddingTop={0} paddingBottom={40}>
            <div className={classes.card}>
                <div className={classes.head}>
                    <OrangeLogo className={classes.logo} />
                    <div>
                        <div className={classes.eyebrow}>
                            <SectionEyebrow variant="card">
                                <FormattedMessage id="redesign.home.dashboard.partner.eyebrow" />
                            </SectionEyebrow>
                        </div>
                        <h2 className={classes.title}>
                            <FormattedMessage id="orange.contentTitle" />
                        </h2>
                        <p className={classes.text}>
                            <FormattedMessage id="orange.contentDescription" />
                        </p>
                        <a href={PLATFORM_URL} target="_blank" rel="noopener noreferrer" className={classes.external}>
                            <FormattedMessage id="orange.linkDescription" />
                            <span aria-hidden="true">↗</span>
                        </a>
                    </div>
                </div>

                <div aria-hidden="true" className={classes.rule} />

                <div className={classes.xrHead}>
                    <div>
                        <h3 className={classes.xrTitle}>
                            <FormattedMessage id="orange.xrContentTitle" />
                        </h3>
                        <p className={classes.xrLead}>
                            <FormattedMessage id="redesign.home.dashboard.partner.xrLead" />
                        </p>
                    </div>
                    <a href={XR_GUIDE_URL} target="_blank" rel="noopener noreferrer" className={classes.xrLink}>
                        <FormattedMessage id="orange.xrLinkDescription" />
                        <span aria-hidden="true">↗</span>
                    </a>
                </div>

                <div className={classes.grid}>
                    {visible.map((resource) => (
                        <a
                            key={resource.id}
                            href={xrUrl(resource.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.resource}
                        >
                            <span className={classes.resourceClass}>
                                <FormattedMessage id={resource.classId} />
                            </span>
                            <h4 className={classes.resourceTitle}>
                                <FormattedMessage id={`orange.xr.${resource.id}.title`} />
                            </h4>
                            <p className={classes.resourceText}>
                                <FormattedMessage id={`orange.xr.${resource.id}.description`} />
                            </p>
                            <span className={classes.tags}>
                                {resource.tags.map((tag) => (
                                    <span key={tag} className={classes.tag}>
                                        <FormattedMessage id={tag} />
                                    </span>
                                ))}
                            </span>
                        </a>
                    ))}
                </div>

                {XR_RESOURCES.length > PREVIEW_COUNT ? (
                    <div className={classes.toggleRow}>
                        <button
                            type="button"
                            aria-expanded={expanded}
                            onClick={() => setExpanded(!expanded)}
                            className={classes.toggle}
                        >
                            <FormattedMessage
                                id={
                                    expanded
                                        ? 'redesign.home.dashboard.partner.showLess'
                                        : 'redesign.home.dashboard.partner.showAll'
                                }
                                values={{ count: XR_RESOURCES.length }}
                            />
                        </button>
                    </div>
                ) : null}
            </div>
        </LandingSection>
    );
};

export default DigitaliadaContent;
